import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import ArticleCard from "@/components/base/ArticleCard";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || "0", 10);
  const article = articles.find((a) => a.id === articleId);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20">
          <div className="container-custom py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-file-unknow-line text-5xl text-foreground-600"></i>
            </div>
            <h1 className="font-heading font-bold text-2xl text-foreground-100 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的文章可能已被删除或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-950 rounded-lg font-medium text-sm transition-all duration-200"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const formattedDate = new Date(article.publishedAt).toLocaleDateString("zh-CN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const formattedViews = article.viewCount >= 10000
    ? `${(article.viewCount / 10000).toFixed(1)}万`
    : article.viewCount.toLocaleString();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishedAt,
    author: {
      "@type": "Person",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: "黑料社区",
      url: "https://heiliao.community",
    },
    image: article.imageUrl,
    url: `https://heiliao.community/article/${article.id}`,
    inLanguage: "zh-CN",
    articleSection: article.category,
    wordCount: article.content.length,
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <Navbar />

      <main className="pt-20">
        {/* Hero Image */}
        <div className="relative h-[300px] md:h-[400px] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={`${article.title} - 黑料社区`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent"></div>
        </div>

        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            {/* Breadcrumb */}
            <div className="my-6">
              <Breadcrumb
                items={[
                  { name: "首页", path: "/" },
                  { name: article.category, path: `/category/${article.categorySlug}` },
                  { name: article.title },
                ]}
              />
            </div>

            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="px-3 py-1 bg-primary-500/10 text-primary-400 text-xs font-medium rounded-full">
                  {article.category}
                </span>
                {article.isHot && (
                  <span className="px-3 py-1 bg-accent-500/10 text-accent-500 text-xs font-medium rounded-full flex items-center gap-1">
                    <i className="ri-fire-line w-3 h-3 flex items-center justify-center"></i>
                    热门
                  </span>
                )}
                {article.isExclusive && (
                  <span className="px-3 py-1 bg-background-200 text-foreground-400 text-xs font-medium rounded-full">
                    独家
                  </span>
                )}
              </div>

              <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-100 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex flex-col sm:flex-row sm:items-center gap-4 text-sm text-foreground-500 pb-6 border-b border-background-200/60">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/10">
                      <i className="ri-user-3-line text-primary-400"></i>
                    </div>
                    <span className="text-foreground-300 font-medium">{article.author}</span>
                  </div>
                  <span className="text-foreground-600">|</span>
                  <span>{formattedDate}</span>
                </div>
                <div className="flex items-center gap-4 sm:ml-auto">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                    {formattedViews} 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line w-4 h-4 flex items-center justify-center"></i>
                    {article.commentCount} 评论
                  </span>
                  <span>{article.readTime} 分钟阅读</span>
                </div>
              </div>
            </header>

            {/* Article Content */}
            <article className="prose-custom mb-12">
              <div
                className="text-foreground-300 leading-relaxed text-base [&>p]:mb-4 [&>p]:leading-loose [&>h3]:font-heading [&>h3]:font-bold [&>h3]:text-xl [&>h3]:text-foreground-100 [&>h3]:mt-8 [&>h3]:mb-4 [&>blockquote]:border-l-4 [&>blockquote]:border-primary-500/50 [&>blockquote]:pl-5 [&>blockquote]:py-2 [&>blockquote]:my-6 [&>blockquote]:text-foreground-400 [&>blockquote]:italic [&>blockquote]:bg-background-100/50 [&>blockquote]:rounded-r-lg"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </article>

            {/* Tags */}
            <div className="flex items-center gap-2 flex-wrap pb-8 border-b border-background-200/60">
              <span className="text-foreground-500 text-sm">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-400 rounded-full text-xs font-medium hover:text-foreground-200 transition-colors cursor-pointer border border-background-200/40"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="mt-12 mb-16">
                <h3 className="font-heading font-bold text-xl text-foreground-100 mb-6 flex items-center gap-2">
                  <i className="ri-links-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                  相关推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <ArticleCard key={rel.id} article={rel} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}